import React, { useRef, useState, useEffect } from 'react';
import { Phase } from '../types';
import { ChevronLeft, ChevronRight, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';

interface PhaseNavigatorProps {
  phases: Phase[];
  activePhaseId: string;
  onSelectPhase: (phaseId: string) => void;
}

const PhaseNavigator: React.FC<PhaseNavigatorProps> = ({ 
  phases, 
  activePhaseId, 
  onSelectPhase 
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  
  // Safe scroll to element function
  const safeScrollIntoView = (element: Element | null) => {
    if (!element || !scrollContainerRef.current) return;
    
    try {
      const container = scrollContainerRef.current;
      const containerRect = container.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      
      // Calculate if element is in view
      const isInView = 
        elementRect.left >= containerRect.left - 20 &&
        elementRect.right <= containerRect.right + 20;
          
      if (!isInView) {
        // Calculate centerpoint
        const scrollLeftTarget = 
          element.getBoundingClientRect().left + 
          container.scrollLeft - 
          containerRect.left - 
          (containerRect.width / 2) + 
          (elementRect.width / 2);
          
        container.scrollTo({
          left: scrollLeftTarget,
          behavior: 'smooth'
        });
      }
    } catch (error) {
      console.error('Error scrolling to phase:', error);
    }
  };
  
  // Check if scrolling controls should be shown
  const checkScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    
    setShowLeftArrow(container.scrollLeft > 10);
    setShowRightArrow(
      container.scrollLeft < container.scrollWidth - container.clientWidth - 10
    );
  };
  
  // Initialize scroll indicators and scroll active phase into view
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    
    // Add scroll event listener
    const handleScroll = () => {
      checkScroll();
    };
    
    // Add resize observer to check scroll when dimensions change
    const resizeObserver = new ResizeObserver(() => {
      checkScroll();
    });
    
    // Initial check and setup
    checkScroll();
    container.addEventListener('scroll', handleScroll, { passive: true });
    resizeObserver.observe(container);
    
    // Scroll active phase into view (with a slight delay to ensure DOM is ready)
    if (activePhaseId) {
      const scrollToActivePhase = () => {
        const activeElement = container.querySelector(`[data-phase-id="${activePhaseId}"]`);
        safeScrollIntoView(activeElement);
      };
      
      // Small timeout to ensure everything is rendered properly
      const timeoutId = setTimeout(scrollToActivePhase, 100);
      
      return () => {
        container.removeEventListener('scroll', handleScroll);
        resizeObserver.disconnect();
        clearTimeout(timeoutId);
      };
    }
    
    return () => {
      container.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
    };
  }, [activePhaseId]);
  
  // Handle scroll button clicks
  const handleScroll = (direction: 'left' | 'right') => {
    const container = scrollContainerRef.current;
    if (!container) return;
    
    const scrollAmount = Math.max(300, container.clientWidth * 0.6);
    const targetScroll = container.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
    
    container.scrollTo({
      left: targetScroll,
      behavior: 'smooth'
    });
  };
  
  // If no phases, don't render
  if (!phases.length) {
    return null;
  }
  
  return (
    <div className="bg-white dark:bg-zinc-800 pb-4 border-b border-zinc-200 dark:border-zinc-700">
      <div className="relative flex items-center">
        {/* Left chevron */}
          <Button
            variant="ghost"
            size="icon"
          className={`absolute -left-2 z-10 h-8 w-8 rounded-full bg-white dark:bg-zinc-800 shadow-sm border border-zinc-100 dark:border-zinc-700 transition-opacity duration-200 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-700 ${
            showLeftArrow ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
            onClick={() => handleScroll('left')}
            aria-label="Scroll left"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
        
        {/* Scrollable phase list */}
        <div 
          ref={scrollContainerRef}
          className="flex overflow-x-auto py-2 px-1 no-scrollbar w-full"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          role="tablist"
          aria-orientation="horizontal"
        >
          {phases.map((phase, index) => {
            const isActive = phase.id === activePhaseId;
            const isCompleted = phase.progress === 100;
            
            return (
              <div 
                key={phase.id}
                data-phase-id={phase.id}
                className={`
                  min-w-[140px] sm:min-w-[170px] flex-shrink-0 cursor-pointer rounded-lg border transition-all duration-200 
                  mx-1 sm:mx-1.5 hover:shadow-sm touch-manipulation
                  ${isActive 
                    ? 'bg-primary text-primary-foreground border-primary shadow-sm' 
                    : isCompleted 
                      ? 'bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 border-green-400 dark:border-green-500 hover:border-green-500 dark:hover:border-green-400' 
                      : 'bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 border-zinc-200 dark:border-zinc-700 hover:border-primary/50 dark:hover:border-primary/50'}
                `}
                onClick={() => onSelectPhase(phase.id)}
                role="tab"
                aria-selected={isActive}
                aria-controls={`phase-content-${phase.id}`}
                tabIndex={isActive ? 0 : -1}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelectPhase(phase.id);
                  }
                }}
              >
                <div className="p-2.5 sm:p-3">
                  <div className="flex items-center justify-between mb-1">
                    <div className={`text-xs font-semibold uppercase ${isActive ? 'text-white/90' : 'text-zinc-600 dark:text-zinc-400'}`}>
                      Phase {index + 1}
                    </div>
                    
                    {/* Completion indicator */}
                    {isCompleted && (
                      <CheckCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-green-500 dark:text-green-400" />
                    )}
                  </div>
                  
                  <div className={`text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 leading-tight ${isActive ? 'text-white' : 'text-zinc-800 dark:text-zinc-200'}`}>
                    {phase.title.replace(/^Phase \d+ -\s*/i, '')}
                  </div>
                  
                  {/* Progress bar and percentage */}
                  <div className="mt-1">
                    <div className="flex justify-between items-center mb-0.5">
                      <div className={`text-xs ${isActive ? 'text-white/90' : 'text-zinc-500 dark:text-zinc-400'}`}>
                        Progress
                      </div>
                      <div className={`text-xs font-medium ${
                        isActive 
                          ? 'text-white' 
                    : isCompleted 
                            ? 'text-green-600 dark:text-green-400' 
                            : 'text-primary dark:text-blue-400'
                      }`}>
                        {phase.progress}%
                      </div>
                    </div>
                    
                    <Progress 
                      value={phase.progress} 
                      className={`
                        h-1 ${
                          isActive 
                            ? 'bg-white/20' 
                            : 'bg-zinc-100 dark:bg-zinc-700'
                        }
                      `}
                      indicatorClassName={`
                        ${isCompleted 
                          ? 'bg-green-500 dark:bg-green-400' 
                          : isActive
                            ? 'bg-white' 
                            : 'bg-primary dark:bg-blue-400'
                        }
                      `}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Right scroll button with gradient fade */}
        <div className={`absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-r from-transparent to-white dark:to-zinc-800 pointer-events-none transition-opacity duration-200 ${showRightArrow ? 'opacity-100' : 'opacity-0'}`}></div>
        
            <Button
              variant="ghost"
              size="icon"
          className={`absolute -right-2 z-10 h-8 w-8 rounded-full bg-white dark:bg-zinc-800 shadow-sm border border-zinc-100 dark:border-zinc-700 transition-opacity duration-200 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-700 ${
            showRightArrow ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
              onClick={() => handleScroll('right')}
              aria-label="Scroll right"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
      </div>
    </div>
  );
};

export default PhaseNavigator; 